import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TReportReduxState } from "./types";
import { TGetInventoryReportCardSummaryAPIResponse, TGetInventoryReportTransactionReceivedListingAPIResponse, TGetInventoryReportTransactionListingFilters, TGetInventoryReportTransactionSummaryFilters, TGetSalesReportCardSummaryAPIResponse, TGetSalesReportTransactionListingAPIResponse, TGetSalesReportTransactionListingFilters, TInventoryReportTransactionListing, TSalesReportTransactionListing, TGetInventoryReportTransactionDeliveredListingAPIResponse, TGetInventoryReportBranchListingAPIResponse, TInventoryBranchListingData } from "~types/feat/report";
import dayjs from "dayjs";
import { FundTypeEnum, ReportInventoryReportSelectedTab } from "~utils/enums";

const getFirstDayOfMonth = () => {
    const today = dayjs();

    const firstDay = today.startOf('month');

    return firstDay.toISOString();

};

const getTodaysDate = () => {
    const today = dayjs().endOf('day').toISOString();

    return today;
};

export const initialState: TReportReduxState = {
    loadings: {
        getSalesReportTransactionListing: false,
        getSalesReportCardSummary: false,
        exporTExportSalesReportListing: false,
        getInventoryReportCardSummary: false,
        getInventoryReceivedReportTransactionListing: false,
        getInventoryDeliveredReportTransactionListing: false,
        getInventoryBranchReportListing: false,
        exportInventoryReceivedReportListing: false,
        exportInventoryDeliveredReportListing: false,
        exportInventoryBranchReportListing: false,
    },
    reportExportSalesReportListingForExport: [],
    salesReport: {
        salesReportTransactionListing: {
            index: 0,
            maxIndex: 0,
            totalCount: 0,
            filters: {
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
                branchCode: '',
                branchName: '',
                metalType: '',
                silverType: '',
                goldType: '',
                transactionType: '',
                productName: '',
                productId: 0,
            },
            appliedFilters: {
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
                branchCode: '',
                branchName: '',
                metalType: '',
                silverType: '',
                goldType: '',
                transactionType: '',
                productName: '',
                productId: 0,
            },
            data: [],
        },
        salesReportCardSummary: {
            totalSales: 0,
            totalTransactions: 0,
            totalAffinSplit: 0,
            totalTTTSplit: 0,
        },
    },
    reportExportInventoryReceivedReportListingForExport: [],
    reportExportInventoryDeliveredReportListingForExport: [],
    reportExportInventoryBranchReportListingForExport: [],
    inventoryReport: {
        inventoryReceivedReportTransactionListing: {
            index: 0,
            maxIndex: 0,
            totalCount: 0,
            filters: {
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
                fundType: '',
                branchCode: '',
                branchName: '',
                productId: 0,
                productName: '',
                metalType: '',
                serialNo: '',
            },
            data: [],
        },
        inventoryDeliveredReportTransactionListing: {
            index: 0,
            maxIndex: 0,
            totalCount: 0,
            filters: {
                dateFrom: getFirstDayOfMonth(),
                dateTo: getTodaysDate(),
                fundType: '',
                branchCode: '',
                branchName: '',
                productId: 0,
                productName: '',
                metalType: '',
                serialNo: '',
            },
            data: [],
        },
        inventoryBranchReportListing: {
            index: 0,
            maxIndex: 0,
            totalCount: 0,
            filters: {
                dateFrom: '',
                dateTo: '',
                fundType: '',
                branchCode: '',
                branchName: '',
                productId: 0,
                productName: '',
                metalType: '',
                serialNo: '',
            },
            data: [],
        },
        inventoryReportListingFilters: {
            dateFrom: getFirstDayOfMonth(),
            dateTo: getTodaysDate(),
            fundType: '',
            branchCode: '',
            branchName: '',
            productId: 0,
            productName: '',
            metalType: '',
            serialNo: '',
        },
        inventoryReportFiltersSummary: {
            fundType: FundTypeEnum.Islamic,
        },
        inventoryReportCardSummary: {
            gold: {
                total: 0,
                allocated: 0,
                available: 0,
            },
            silver: {
                total: 0,
                allocated: 0,
                available: 0,
            },
        },
        selectedInventoryReportTab: ReportInventoryReportSelectedTab.InventoryReceived,
    },
    errors: {
        getSalesReportTransactionListing: '',
        getSalesReportCardSummary: '',
        exporTExportSalesReportListing: '',
        getInventoryReportCardSummary: '',
        getInventoryReceivedReportTransactionListing: '',
        getInventoryDeliveredReportTransactionListing: '',
        getInventoryBranchReportListing: '',
        exportInventoryReceivedReportListing: '',
        exportInventoryDeliveredReportListing: '',
        exportInventoryBranchReportListing: '',
    },
}

const reportSlice = createSlice({
    name: 'report',
    initialState,
    reducers: {
        // sales report
        reportGetSalesReportTransactionListingAttempt: (state) => {
            state.loadings.getSalesReportTransactionListing = true;
            state.errors.getSalesReportTransactionListing = '';
        },
        reportGetSalesReportTransactionListingSuccess: (state, action: PayloadAction<TGetSalesReportTransactionListingAPIResponse>) => {
            state.loadings.getSalesReportTransactionListing = false;
            state.errors.getSalesReportTransactionListing = '';
            state.salesReport.salesReportTransactionListing = {
                filters: state.salesReport.salesReportTransactionListing.filters,
                appliedFilters: state.salesReport.salesReportTransactionListing.appliedFilters,
                ...action.payload,
            };
        },
        reportGetSalesReportTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSalesReportTransactionListing = false;
            if (action.payload) state.errors.getSalesReportTransactionListing = action.payload;
        },

        reportGetSalesReportCardSummaryAttempt: (state) => {
            state.loadings.getSalesReportCardSummary = true;
            state.errors.getSalesReportCardSummary = '';
        },
        reportGetSalesReportCardSummarySuccess: (state, action: PayloadAction<TGetSalesReportCardSummaryAPIResponse>) => {
            state.loadings.getSalesReportCardSummary = false;
            state.errors.getSalesReportCardSummary = '';
            state.salesReport.salesReportCardSummary = { ...action.payload };
        },
        reportGetSalesReportCardSummaryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getSalesReportCardSummary = false;
            if (action.payload) state.errors.getSalesReportCardSummary = action.payload;
        },

        reportSetSalesReportTransactionListingFilters: (state, action: PayloadAction<TGetSalesReportTransactionListingFilters>) => {
            state.salesReport.salesReportTransactionListing.filters = action.payload;
        },
        reportSetSalesReportListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.salesReport.salesReportTransactionListing.index = index;
        },
        reportSetSalesReportAppliedFilters: (state, action: PayloadAction<TGetSalesReportTransactionListingFilters>) => {
            state.salesReport.salesReportTransactionListing.appliedFilters = action.payload;
        },

        reportGetSalesReportExportAttempt: (state) => {
            state.loadings.exporTExportSalesReportListing = true;
            state.errors.exporTExportSalesReportListing = '';
        },
        reportGetSalesReportExportSuccess: (state, action: PayloadAction<TSalesReportTransactionListing[]>) => {
            state.loadings.exporTExportSalesReportListing = false;
            state.reportExportSalesReportListingForExport = action.payload;
        },
        reportGetSalesReportExportFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exporTExportSalesReportListing = false;
            if (action.payload) state.errors.exporTExportSalesReportListing = action.payload;
        },
        reportClearSalesReportListingForExport: (state) => {
            state.reportExportSalesReportListingForExport = initialState.reportExportSalesReportListingForExport;
        },

        // inventory report
        reportGetInventoryReceivedReportTransactionListingAttempt: (state) => {
            state.loadings.getInventoryReceivedReportTransactionListing = true;
            state.errors.getInventoryReceivedReportTransactionListing = '';
        },
        reportGetInventoryReceivedReportTransactionListingSuccess: (state, action: PayloadAction<TGetInventoryReportTransactionReceivedListingAPIResponse>) => {
            state.loadings.getInventoryReceivedReportTransactionListing = false;
            state.errors.getInventoryReceivedReportTransactionListing = '';
            state.inventoryReport.inventoryReceivedReportTransactionListing = {
                filters: state.inventoryReport.inventoryReceivedReportTransactionListing.filters,
                ...action.payload,
            };
        },
        reportGetInventoryReceivedReportTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryReceivedReportTransactionListing = false;
            if (action.payload) state.errors.getInventoryReceivedReportTransactionListing = action.payload;
        },

        reportGetInventoryDeliveredReportTransactionListingAttempt: (state) => {
            state.loadings.getInventoryDeliveredReportTransactionListing = true;
            state.errors.getInventoryDeliveredReportTransactionListing = '';
        },
        reportGetInventoryDeliveredReportTransactionListingSuccess: (state, action: PayloadAction<TGetInventoryReportTransactionDeliveredListingAPIResponse>) => {
            state.loadings.getInventoryDeliveredReportTransactionListing = false;
            state.errors.getInventoryDeliveredReportTransactionListing = '';
            state.inventoryReport.inventoryDeliveredReportTransactionListing = {
                filters: state.inventoryReport.inventoryDeliveredReportTransactionListing.filters,
                ...action.payload,
            };
        },
        reportGetInventoryDeliveredReportTransactionListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryDeliveredReportTransactionListing = false;
            if (action.payload) state.errors.getInventoryDeliveredReportTransactionListing = action.payload;
        },

        reportGetInventoryBranchReportListingAttempt: (state) => {
            state.loadings.getInventoryBranchReportListing = true;
            state.errors.getInventoryBranchReportListing = '';
        },
        reportGetInventoryBranchReportListingSuccess: (state, action: PayloadAction<TGetInventoryReportBranchListingAPIResponse>) => {
            state.loadings.getInventoryBranchReportListing = false;
            state.errors.getInventoryBranchReportListing = '';
            state.inventoryReport.inventoryBranchReportListing = {
                filters: state.inventoryReport.inventoryBranchReportListing.filters,
                ...action.payload,
            };
        },
        reportGetInventoryBranchReportListingFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryBranchReportListing = false;
            if (action.payload) state.errors.getInventoryBranchReportListing = action.payload;
        },

        reportSetSelectedInventoryReportTab: (state, action: PayloadAction<ReportInventoryReportSelectedTab>) => {
            state.inventoryReport.selectedInventoryReportTab = action.payload;
        },
        reportSetInventoryReportTransactionListingFilters: (state, action: PayloadAction<TGetInventoryReportTransactionListingFilters>) => {
            state.inventoryReport.inventoryReceivedReportTransactionListing.filters = action.payload;
            state.inventoryReport.inventoryDeliveredReportTransactionListing.filters = action.payload;
            state.inventoryReport.inventoryBranchReportListing.filters = action.payload;
        },

        reportSetInventoryReceivedReportListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.inventoryReport.inventoryReceivedReportTransactionListing.index = index;
        },
        reportSetInventoryDeliveredReportListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.inventoryReport.inventoryDeliveredReportTransactionListing.index = index;
        },
        reportSetInventoryBranchReportListingIndex: (state, action: PayloadAction<{ index: number }>) => {
            const { index } = action.payload;
            state.inventoryReport.inventoryBranchReportListing.index = index;
        },

        reportGetInventoryReportCardSummaryAttempt: (state) => {
            state.loadings.getInventoryReportCardSummary = true;
            state.errors.getInventoryReportCardSummary = '';
        },
        reportGetInventoryReportCardSummarySuccess: (state, action: PayloadAction<TGetInventoryReportCardSummaryAPIResponse>) => {
            state.loadings.getInventoryReportCardSummary = false;
            state.errors.getInventoryReportCardSummary = '';
            state.inventoryReport.inventoryReportCardSummary = { ...action.payload };
        },
        reportGetInventoryReportCardSummaryFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.getInventoryReportCardSummary = false;
            if (action.payload) state.errors.getInventoryReportCardSummary = action.payload;
        },

        reportSetInventoryReportTransactionSummaryFilters: (state, action: PayloadAction<TGetInventoryReportTransactionSummaryFilters>) => {
            state.inventoryReport.inventoryReportFiltersSummary = action.payload;
        },

        reportGetInventoryReceivedReportExportAttempt: (state) => {
            state.loadings.exportInventoryReceivedReportListing = true;
            state.errors.exportInventoryReceivedReportListing = '';
        },
        reportGetInventoryReceivedReportExportSuccess: (state, action: PayloadAction<TInventoryReportTransactionListing[]>) => {
            state.loadings.exportInventoryReceivedReportListing = false;
            state.reportExportInventoryReceivedReportListingForExport = action.payload;
        },
        reportGetInventoryReceivedReportExportFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryReceivedReportListing = false;
            if (action.payload) state.errors.exportInventoryReceivedReportListing = action.payload;
        },
        reportClearInventoryReceivedReportListingForExport: (state) => {
            state.reportExportInventoryReceivedReportListingForExport = initialState.reportExportInventoryReceivedReportListingForExport;
        },

        reportGetInventoryDeliveredReportExportAttempt: (state) => {
            state.loadings.exportInventoryDeliveredReportListing = true;
            state.errors.exportInventoryDeliveredReportListing = '';
        },
        reportGetInventoryDeliveredReportExportSuccess: (state, action: PayloadAction<TInventoryReportTransactionListing[]>) => {
            state.loadings.exportInventoryDeliveredReportListing = false;
            state.reportExportInventoryDeliveredReportListingForExport = action.payload;
        },
        reportGetInventoryDeliveredReportExportFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryDeliveredReportListing = false;
            if (action.payload) state.errors.exportInventoryDeliveredReportListing = action.payload;
        },
        reportClearInventoryDeliveredReportListingForExport: (state) => {
            state.reportExportInventoryDeliveredReportListingForExport = initialState.reportExportInventoryDeliveredReportListingForExport;
        },

        reportGetInventoryBranchReportExportAttempt: (state) => {
            state.loadings.exportInventoryBranchReportListing = true;
            state.errors.exportInventoryBranchReportListing = '';
        },
        reportGetInventoryBranchReportExportSuccess: (state, action: PayloadAction<TInventoryBranchListingData[]>) => {
            state.loadings.exportInventoryBranchReportListing = false;
            state.reportExportInventoryBranchReportListingForExport = action.payload;
        },
        reportGetInventoryBranchReportExportFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loadings.exportInventoryBranchReportListing = false;
            if (action.payload) state.errors.exportInventoryBranchReportListing = action.payload;
        },
        reportClearInventoryBranchReportListingForExport: (state) => {
            state.reportExportInventoryBranchReportListingForExport = initialState.reportExportInventoryBranchReportListingForExport;
        },
    }
});

export default {
    actions: reportSlice.actions,
    reducers: reportSlice.reducer,
}